import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const BillingTerms = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#2D0052]">
              Payment & Billing Terms
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Last updated: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                1. Definitions
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Gig Fee</strong>: The total amount agreed between
                  Client and Creator for services rendered.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Platform Fee</strong>: The commission, service
                  charge, or transaction fee retained by Oye Creators.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Billing Cycle</strong>: The period for which
                  recurring fees (if any) are calculated and invoiced.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Due Date</strong>: The date by which a payment must
                  be received.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>GST</strong>: Goods and Services Tax levied under
                  Indian Law (CGST/SGST/IGST as applicable).
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                2. Billing Cycles & Invoicing
              </h2>
              <ol className="list-decimal ml-6 space-y-4">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>One-Time Gigs</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Upon Client confirmation of a Creator's performance, an
                      invoice is generated immediately.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      Invoice payment is due within 7 days of issuance, unless
                      otherwise agreed in writing.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Recurring Services</strong> (if applicable)
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      For any monthly/quarterly subscription or retainer, the
                      Billing Cycle begins on the date of subscription
                      activation.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      Recurring invoices are issued on the same calendar date
                      each cycle.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Invoice Delivery</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      All invoices are delivered electronically to the email
                      address on file.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      It is the user's responsibility to keep billing contact
                      details current.
                    </li>
                  </ul>
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                3. Accepted Payment Methods
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>
                    Electronic Funds Transfer (EFT/NEFT/RTGS/IMPS)
                  </strong>{" "}
                  to the Platform's designated bank account.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Credit/Debit Card</strong> payments via our secure
                  payment gateway.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>UPI</strong> or other digital wallets approved by
                  the Platform.
                </li>
              </ul>
              <p className="text-base leading-relaxed text-gray-800 mt-4">
                Any transaction fees or bank charges arising from electronic
                transfers shall be borne by the paying party.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                4. Fees & Charges
              </h2>
              <ol className="list-decimal ml-6 space-y-4">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Platform Fee</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Oye Creators retains a percentage of the Gig Fee (e.g.,
                      10–20%), as disclosed before confirmation.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Transaction Charges</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Any gateway, currency conversion, or remittance fees
                      will be added to the invoice total.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Chargeback Fees</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      In the event of a reverse transaction or chargeback, the
                      initiating party shall reimburse Oye Creators for any
                      incurred fees plus a ₹500 administration charge.
                    </li>
                  </ul>
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                5. Refund Policy
              </h2>
              <ol className="list-decimal ml-6 space-y-4">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>By Clients</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Cancellation more than 5 days before start date: full
                      refund of Gig Fee minus a 5% administrative charge.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      Cancellation 2–5 days before start date: 50% refund.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      Cancellation less than 48 hours before start date: no
                      refund.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>By Creators</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      If a Creator fails to deliver as per the agreed scope,
                      Clients may request partial or full refund, subject to
                      review by Oye Creators.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Processing Time</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Approved refunds will be credited back via the original
                      payment method within 14 business days.
                    </li>
                  </ul>
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                6. Late Payments & Default
              </h2>
              <ol className="list-decimal ml-6 space-y-4">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Payment Due Date</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      If payment is not received by the Due Date, the
                      outstanding amount will accrue interest at 1.5% per
                      month (or the maximum permissible under Indian law,
                      whichever is lower).
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Service Suspension</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      For Clients: non-payment after 15 days—Platform access
                      may be suspended until full settlement.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      For Creators: non-receipt of payment by 30 days—Platform
                      will investigate and may temporarily withhold new gig
                      assignments.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Collection Costs</strong>
                  <ul className="list-disc ml-6 mt-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Defaulting parties shall indemnify Oye Creators for any
                      reasonable legal or collection costs incurred to recover
                      overdue amounts.
                    </li>
                  </ul>
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                7. Taxes & Withholding
              </h2>
              <ol className="list-decimal ml-6 space-y-4">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>GST and Other Taxes (India)</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      All fees payable under these Terms are exclusive of GST.
                      Oye Creators will charge and collect GST where
                      applicable under the CGST/SGST/IGST laws.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      Creators responsible for registering for GST if turnover
                      thresholds are met; Clients responsible for any input
                      tax credit claims.
                    </li>
                  </ul>
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>International Taxes</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-2">
                    <li className="text-base leading-relaxed text-gray-800">
                      Non-resident users may be subject to withholding tax
                      under Indian Income-tax Act, 1961. Platform may deduct
                      taxes at the applicable rate unless exemption
                      certificates are provided.
                    </li>
                    <li className="text-base leading-relaxed text-gray-800">
                      Users are responsible for compliance with their home
                      country's tax obligations (e.g., VAT, sales tax).
                    </li>
                  </ul>
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                8. Disputes & Chargebacks
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Dispute Resolution</strong>: Any billing dispute
                  must be raised in writing within 10 days of invoice date.
                  Parties shall first attempt informal resolution via Platform
                  support.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Chargebacks</strong>: Unauthorized chargebacks or
                  payment reversals without following the dispute process will
                  be treated as defaults, invoking the late-payment and
                  collection provisions above.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                9. Indemnification & Liability
              </h2>
              <ol className="list-decimal ml-6 space-y-4">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Indemnification</strong>: Each User shall indemnify,
                  defend, and hold harmless Oye Creators and its employees,
                  officers, and agents from any claims, damages, or expenses
                  arising out of non-payment, breach of these Terms, or tax
                  liabilities related to their transactions.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Limitation of Liability</strong>: To the fullest
                  extent permitted by law, Oye Creators' aggregate liability
                  for any claim arising from these Terms shall not exceed the
                  total fees paid by the Client or Creator in the preceding 12
                  months.
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                10. Governing Law & Jurisdiction
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                These Terms shall be governed by the laws of India. Exclusive
                jurisdiction lies with the courts of Pune, Maharashtra, India,
                unless otherwise required by mandatory international law.
              </p>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default BillingTerms
